// src/pages/RegisterLanding.js
//
// Entry point for the pharmacy QR self-registration link:
//   fitme.lk/register?pharmacy=<org_id>
//
// Decision tree on load:
//   - Already logged in + phone verified  -> just link/create the
//     Customer record for this pharmacy and go straight to /home.
//     No form, no OTP — they've done all that before.
//   - Logged in but phone not verified yet -> continue to /verify-phone
//     (same as a fresh registration), passing pharmacy_id through.
//   - Not logged in at all -> show a simple screen with "Continue with
//     Google" front and center (fastest path for a customer standing at
//     a pharmacy counter), with a small fallback link to the full
//     email/password form for people without Google.

import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { Message } from "primereact/message";
import api from "../api/client";
import { AuthContext } from "../auth/AuthContext";
import Register from "./Register";
import { usePortalData } from "../portal/context/PortalDataContext";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const S = {
  page: {
    minHeight: "100dvh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(155deg, #0d4f7a 0%, #1a6fa8 45%, #0d9488 100%)",
    padding: "20px 16px",
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: "#ffffff",
    borderRadius: 20,
    padding: "36px 32px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    textAlign: "center",
  },
  heading: {
    fontSize: 18,
    fontWeight: 600,
    color: "#0f2944",
    margin: "0 0 6px",
    fontFamily: "'Lora', Georgia, serif",
  },
  sub: {
    fontSize: 13,
    color: "#4a6080",
    margin: "0 0 24px",
    lineHeight: 1.5,
  },
  fallbackLink: {
    marginTop: 20,
    fontSize: 13,
    color: "#1a6fa8",
    fontWeight: 600,
    cursor: "pointer",
    background: "none",
    border: "none",
  },
  spinner: {
    fontSize: 28,
    color: "#1a6fa8",
  },
};

export default function RegisterLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pharmacyIdFromUrl = searchParams.get("pharmacy") || "";
  const departmentIdFromUrl = searchParams.get("department") || "";
  const { token, phoneVerified, isInitializing, login, selectOrg, selectDepartment } = useContext(AuthContext);
  const { refresh } = usePortalData();

  // Persist to localStorage as a fallback — React Router's location.state
  // (used to carry pharmacyId/departmentId to /verify-phone) doesn't
  // survive a full page refresh, e.g. if the person reloads mid-OTP-entry.
  // Falling back to this means a refresh doesn't silently drop the link.
  useEffect(() => {
    if (pharmacyIdFromUrl) {
      localStorage.setItem("pendingPharmacyId", pharmacyIdFromUrl);
    }
    if (departmentIdFromUrl) {
      localStorage.setItem("pendingDepartmentId", departmentIdFromUrl);
    }
  }, [pharmacyIdFromUrl, departmentIdFromUrl]);

  const pharmacyId = pharmacyIdFromUrl || localStorage.getItem("pendingPharmacyId") || "";
  const departmentId = departmentIdFromUrl || localStorage.getItem("pendingDepartmentId") || "";

  const [linking, setLinking] = useState(false);
  const [error, setError] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  // Links the current (already-authenticated) account to this pharmacy,
  // then sends them home. Only reachable once phone_verified is true.
  //
  // Two things must happen for the new pharmacy to actually show up
  // correctly, not just get created server-side:
  //   1. refresh() — PortalDataContext only refetches the pharmacy list
  //      on token change or an explicit refresh() call, never reactively
  //      after a link like this.
  //   2. selectOrg(pharmacyId) — PharmacySelector.js auto-selects whatever
  //      org_id is already cached in localStorage over any newly-added
  //      pharmacy, so without this the customer would still land on
  //      their OLD pharmacy by default even once the new one appears
  //      in the list.
  const linkAndContinue = async () => {
    if (!pharmacyId) { navigate("/home"); return; }
    setLinking(true);
    try {
      await api.post("pharmacies/link/", { pharmacy_id: pharmacyId, department_id: departmentId || undefined });
      localStorage.removeItem("pendingPharmacyId");
      localStorage.removeItem("pendingDepartmentId");
      selectOrg(pharmacyId);
      if (departmentId) selectDepartment(departmentId);
      refresh();
      navigate("/home");
    } catch (err) {
      // If the cached phoneVerified flag was stale (server disagrees),
      // don't just swallow it and dump them on /home unlinked — send
      // them through phone verification instead, same as a fresh signup.
      if (err.response?.data?.phone_verified === false) {
        navigate("/verify-phone", { state: { fromRegistration: true, pharmacyId, departmentId } });
        return;
      }
      // Any other failure (network blip, pharmacy_id invalid, etc.) — no
      // new data was created, so a plain SPA navigation is fine here.
      console.error("Pharmacy link failed:", err);
      navigate("/home");
    } finally {
      setLinking(false);
    }
  };

  useEffect(() => {
    if (isInitializing) return;   // wait for AuthContext's token check against /me/
    if (showEmailForm) return;    // person explicitly chose the form — don't redirect them away

    if (token) {
      if (phoneVerified) {
        linkAndContinue();
      } else {
        navigate("/verify-phone", { state: { fromRegistration: true, pharmacyId, departmentId } });
      }
    }
    // else: not logged in at all — render the Google/email choice below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, token, phoneVerified, showEmailForm]);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setGoogleLoading(true);
    try {
      const res = await api.post("auth/google/", { id_token: credentialResponse.credential });
      login(res.data.token, res.data.phone_verified, res.data.refresh_token);
      if (res.data.phone_verified) {
        await api.post("pharmacies/link/", { pharmacy_id: pharmacyId, department_id: departmentId || undefined }).catch(() => {});
        localStorage.removeItem("pendingPharmacyId");
        localStorage.removeItem("pendingDepartmentId");
        selectOrg(pharmacyId);
        if (departmentId) selectDepartment(departmentId);
        refresh();
        navigate("/home");
      } else {
        navigate("/verify-phone", { state: { fromRegistration: true, pharmacyId, departmentId } });
      }
    } catch (err) {
      setError(err.response?.data?.error ?? "Google sign-in failed. Please try again.");
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError("Google sign-in was cancelled or failed. Please try again.");
  };

  // Already logged in — show a lightweight "linking you up" spinner
  // rather than any form, while the effect above does its thing.
  if (isInitializing || (token && !showEmailForm)) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <i className="pi pi-spin pi-spinner" style={S.spinner} />
          <p style={{ ...S.sub, marginTop: 16, marginBottom: 0 }}>
            {linking ? "Linking your account with this pharmacy…" : "One moment…"}
          </p>
        </div>
      </div>
    );
  }

  // Explicit fallback to the full email/password registration form —
  // still tagged with ?pharmacy= in the URL, so Register.js's own
  // pharmacyId handling (added earlier) works unchanged.
  if (showEmailForm) {
    return <Register />;
  }

  // Not logged in — Google-first landing screen.
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <div style={S.page}>
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&family=Lora:wght@600&display=swap" rel="stylesheet" />
        <div style={S.card}>
          <p style={S.heading}>Welcome to FitMe</p>
          <p style={S.sub}>Sign in to link your account with this pharmacy — takes a few seconds.</p>

          {error && (
            <div style={{ marginBottom: 16 }}>
              <Message severity="error" text={error} style={{ width: "100%", borderRadius: 10 }} />
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "center", opacity: googleLoading ? 0.7 : 1 }}>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="continue_with"
              shape="rectangular"
              logo_alignment="left"
              width={320}
              theme="outline"
            />
          </div>

          <button style={S.fallbackLink} onClick={() => setShowEmailForm(true)}>
            Don't use Google? Sign up with email instead
          </button>
        </div>

        <div style={{ marginTop: 24, fontSize: 11.5, color: "rgba(255,255,255,0.5)", textAlign: "center" }}>
          Secure customer portal · Powered by Coffer Smart
        </div>
      </div>
    </GoogleOAuthProvider>
  );
}